import ResourceController from "controllers/ResourceController";
import Market from "model/market";
import City from "./city";
import Territory from "./territory";
import passiveControl from "model/actions/city/passiveControl";
import Debug from "debug";
const debug = Debug("model.Political");

class Political {
  static RAID_INFAMY = 1;
  static RAID_INFAMY_LOCAL = 4;

  static atStep(map, political, step) {
    const terr = map.terr;
    const position = map.route[Math.min(step, map.route.length - 1)];
    const thisTerr = terr[position];

    const last = step > 0 ? map.route[step - 1] : null;
    const lastTerr = last ? terr[last] : null;
    const crossedBorder = lastTerr !== thisTerr;
    const isHome = step === 0;

    const city = political.getCityByPosition(position)?.name;
    const territory = political.getTerrByPosition(thisTerr)?.name;

    return {
      isHome,
      city,
      territory,
      crossedBorder,
    };
  }

  static nextCityDistance(map, political, currStep) {
    const sortedCities = political.roadCities.sort((a, b) => a.step - b.step);
    for (let i = 0; i < sortedCities.length; i++) {
      const city = sortedCities[i];
      if (city.step > currStep) return city.step - currStep;
    }
  }

  static init(map) {
    const cities = new Map();
    map.political.forEach((value) => {
      cities.set(value.cityName, new City(value));
    });

    const territories = new Map();
    Array.from(cities.values())
      .filter((city) => city.isCapital)
      .forEach((city) => {
        territories.set(
          city.territory,
          new Territory(city.territory, city.name, city.position),
        );
      });

    const result = new Political(cities, territories);
    debug(result);
    return result;
  }
  constructor(cities, territories) {
    this.cities = cities;
    this.territories = territories;
    this.roadCities = Array.from(cities.values())
      .filter((city) => city.step !== -1)
      .sort((a, b) => a.step - b.step)
      .map((city, i, arr) => ({
        ...city,
        nextCity: i === arr.length - 1 ? null : arr[i + 1],
        distanceToNextCity: i === arr.length - 1
          ? -1
          : arr[i + 1].step - city.step,
      }));
  }

  getTerrByPosition(position) {
    let result = null;
    this.territories.forEach((value) => {
      if (value.capitalPosition === position) result = value;
    });

    return result;
  }

  getCityByPosition(position) {
    let result = null;
    this.cities.forEach((value) => {
      if (value.position === position) result = value;
    });

    return result;
  }

  updateStatusMetrics(cityName, metrics) {
    if (!cityName) return {};

    const city = this.cities.get(cityName);
    const oldStatus = city.getStatus();
    const newStatus = city.updateStatusMetrics(metrics);
    const isNewStatus = oldStatus !== newStatus;
    if (isNewStatus) {
      // Register daily gain actions
      debug(
        `City ${cityName} status changed from ${oldStatus} to ${newStatus}`,
      );
      ResourceController.instance().registerAction(
        passiveControl(cityName, newStatus),
      );
    }
    return { status: newStatus, isNewStatus };
  }

  /**
   * Puts the towns' side of the daily ledger back after ResourceController.resetDailyEffects.
   *
   * A new run replaces the party but not the world — GameContext carries `political` across —
   * so the standings the last man earned are still on the cities. They are registered only at
   * the moment they change (updateStatusMetrics), so a standing that survives a death would
   * otherwise sit there paying nothing until it happened to move again.
   */
  restoreControl() {
    this.getCitiesControlled().forEach((city) =>
      ResourceController.instance().registerAction(
        passiveControl(city.name, city.getStatus()),
      )
    );
  }

  getCitiesControlled() {
    const cities = Array.from(this.cities.values()).filter(
      (city) =>
        city.getStatus() === City.StatusInTown.Dominant ||
        city.getStatus() === City.StatusInTown.Familiar,
    );
    debug(cities);
    return cities;
  }

  market(party, calendar) {
    const cityName = party.city;

    if (cityName) {
      return this.cities.get(cityName).getMarket();
    }

    const generosity = City.Generosity.Fair;
    const attitude = this.attitude(
      party.territory,
      party.traits.getTraitValue("charisma"),
    );

    // TODO: Enhance Traveling merchant market
    return new Market({
      season: calendar.season(),
      generosity,
      friendliness: attitude,
    });
  }

  prices(party, calendar) {
    const cityName = party.city;

    if (cityName) {
      this.cities.get(cityName).updateMarket(party, this, calendar);
      return this.cities.get(cityName).getMarket().prices;
    }

    const generosity = City.Generosity.Fair;
    const attitude = this.attitude(
      party.territory,
      party.traits.getTraitValue("charisma"),
    );
    // TODO: Traveling merchant market
    return new Market({
      season: calendar.season,
      generosity,
      friendliness: attitude,
    }).prices;
  }

  generosity(cityName) {
    return this.cities.get(cityName).generosity;
  }

  attitude(territory, charisma) {
    return this.territories.get(territory).attitude(charisma);
  }

  raid(territory, charisma) {
    debug(`Raiding in ${territory} with charisma ${charisma}`);
    this.territories.forEach((value, key, map) => {
      const infamy = key === territory
        ? Political.RAID_INFAMY_LOCAL
        : Political.RAID_INFAMY;
      const withCharisma = infamy; //TODO: incorporate charisma;
      value.relation = Math.max(0, value.relation - withCharisma);
      map.set(key, value);
    });
  }
}

export default Political;
